
import { useState, useEffect } from "react";
import { isBefore } from "date-fns";
import { calculateEndDate, calculateDuration } from "../../utils/dateUtils";

export const useTaskForm = (selectedProject: any, selectedTask: any, setSelectedTask: (task: any) => void) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarEndOpen, setCalendarEndOpen] = useState(false);
  const [availableTasks, setAvailableTasks] = useState<{id: string, name: string}[]>([]);
  const [selectedDependencies, setSelectedDependencies] = useState<string[]>([]);
  
  // Effect to parse and set selected dependencies when selectedTask changes
  useEffect(() => {
    if (selectedTask && selectedTask.dependencies) {
      const deps = typeof selectedTask.dependencies === 'string'
        ? selectedTask.dependencies.split(',').map(d => d.trim()).filter(Boolean)
        : Array.isArray(selectedTask.dependencies)
          ? selectedTask.dependencies
          : [];
      
      setSelectedDependencies(deps);
    } else {
      setSelectedDependencies([]);
    }
  }, [selectedTask]);
  
  // Initialize available tasks from the project
  useEffect(() => {
    if (selectedProject && selectedProject.tasks) {
      if (selectedTask) {
        updateAvailableTasks();
      }
    }
  }, [selectedProject, selectedTask]);
  
  // Update available tasks based on current task's start date
  const updateAvailableTasks = () => {
    if (!selectedProject || !selectedProject.tasks) return;
    
    const currentTaskStartDate = selectedTask?.startDate ? new Date(selectedTask.startDate) : null;
    const currentTaskIndex = selectedTask?.index;
    
    const tasks = selectedProject.tasks
      .map((task, index) => ({
        id: `task-${index}`,
        name: task.name,
        startDate: task.startDate ? new Date(task.startDate) : null,
        index
      }))
      .filter(task => {
        if (currentTaskIndex === task.index) return false;
        
        if (currentTaskStartDate && task.startDate) {
          return isBefore(task.startDate, currentTaskStartDate);
        }
        
        return true;
      });
    
    setAvailableTasks(tasks);
  };
  
  // Handle task field changes
  const handleChange = (field: string, value: any) => {
    setSelectedTask({
      ...selectedTask,
      [field]: value
    });
    
    if (field === 'duration' && selectedTask.startDate) {
      const startDate = selectedTask.startDate;
      const newEndDate = calculateEndDate(
        startDate, 
        parseInt(value) || 1, 
        selectedProject.excludeWeekends
      );
      setSelectedTask({
        ...selectedTask,
        duration: value,
        endDate: newEndDate
      });
    }
    
    if (field === 'startDate' && selectedTask.duration) {
      const duration = parseInt(selectedTask.duration) || 1;
      const newEndDate = calculateEndDate(
        value, 
        duration, 
        selectedProject.excludeWeekends
      );
      setSelectedTask({
        ...selectedTask,
        startDate: value,
        endDate: newEndDate
      });
      
      setTimeout(() => updateAvailableTasks(), 0);
    }
    
    if (field === 'endDate' && selectedTask.startDate) {
      const newDuration = calculateDuration(
        selectedTask.startDate,
        value,
        selectedProject.excludeWeekends
      );
      setSelectedTask({
        ...selectedTask,
        endDate: value,
        duration: newDuration
      });
    }
  };
  
  // Handle dependency selection
  const handleDependencyChange = (taskId: string) => {
    let newDependencies = [...selectedDependencies];
    
    if (newDependencies.includes(taskId)) {
      newDependencies = newDependencies.filter(id => id !== taskId);
    } else {
      newDependencies.push(taskId);
    }
    
    setSelectedDependencies(newDependencies);
    
    setSelectedTask({
      ...selectedTask,
      dependencies: newDependencies.join(',')
    });
  };

  return {
    calendarOpen,
    setCalendarOpen,
    calendarEndOpen,
    setCalendarEndOpen,
    availableTasks,
    selectedDependencies,
    handleChange,
    handleDependencyChange
  };
};
